/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { makeAutoObservable } from "mobx";

import { Region, getConfig } from "config/config";

export class DevEnvironmentSettingsStore {
  isSettingMobile = false;
  isSettingSumsub = false;
  isFillingDirector = false;
  isFetchingAlias = false;
  lastError: string | null = null;
  lastSuccess: string | null = null;
  alias: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  /**
   * Get region-specific E2E mobile number
   * Matches the pattern from e2e-ts/shared/utils/environment.ts
   */
  private getE2EMobileNumber(): string {
    const config = getConfig();
    return config.region === Region.AU ? "+61412345678" : "+447418632710";
  }

  /**
   * Get backend environment from VITE_ANNA_DEV_ENV
   * Falls back to config if not available
   */
  private getBackendEnv(): string {
    return import.meta.env.VITE_ANNA_DEV_ENV || "expstudio.k2";
  }

  /**
   * Get user alias from localStorage or store
   */
  private getUserAlias(): string | null {
    return this.alias || localStorage.getItem("userAlias");
  }

  /**
   * Fetch and store user alias from /me endpoint
   * Similar to getToken from e2e-ts/shared/api/token.ts
   */
  async fetchAndStoreAlias(): Promise<void> {
    // Only run in non-production
    if (import.meta.env.MODE === "production") {
      console.log("Skipping alias fetch in production mode");
      return;
    }

    this.isFetchingAlias = true;

    try {
      // Get the access token from localStorage
      const annaToken = localStorage.getItem("anna_access_token");
      if (!annaToken) {
        console.log("No anna_access_token found in localStorage");
        this.isFetchingAlias = false;
        return;
      }

      const parsedToken = JSON.parse(annaToken);
      const tokenData = parsedToken.data; // Extract the JWT from the data field

      if (!tokenData) {
        throw new Error("No access token data found");
      }

      const backendEnv = this.getBackendEnv();
      const apiUrl = `https://api-${backendEnv}.anna.money/api/formations/me`;

      console.log("Fetching alias from:", apiUrl);

      const response = await fetch(apiUrl, {
        headers: {
          authorization: `Bearer ${tokenData}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch alias: ${response.status}`);
      }

      const responseData = await response.json();
      console.log("API Response:", responseData);

      const fetchedAlias = responseData.data.alias;

      if (fetchedAlias) {
        this.alias = fetchedAlias;
        // Store in localStorage for other uses
        localStorage.setItem("userAlias", fetchedAlias);
        console.log("Alias stored successfully:", fetchedAlias);
      }
    } catch (error) {
      console.error("Failed to fetch alias:", error);
      // Don't set lastError here as this is background initialization
    } finally {
      this.isFetchingAlias = false;
    }
  }

  /**
   * Set E2E mobile number via internal API
   * Similar to setE2EMobile from e2e-ts/shared/utils/authFlow.ts
   */
  async setE2EMobile(): Promise<void> {
    this.isSettingMobile = true;
    this.lastError = null;
    this.lastSuccess = null;

    try {
      const alias = this.getUserAlias();
      if (!alias) {
        throw new Error("User alias not found in localStorage. Please authenticate first.");
      }

      const backendEnv = this.getBackendEnv();
      const mobile = this.getE2EMobileNumber();
      const mobileUrl = `https://formations-private-${backendEnv}.anna.money/api/internal/e2e/set-mobile`;

      console.log("Setting E2E mobile number:", mobile);
      console.log("Using alias:", alias);
      console.log("Backend environment:", backendEnv);

      const response = await fetch(mobileUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          alias,
          mobile,
        }),
      });

      console.log("Set mobile response status:", response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Failed to set mobile:", response.status, errorText);
        throw new Error(`Failed to set mobile: ${response.status} - ${errorText}`);
      }

      const responseText = await response.text();
      console.log("Set mobile response body:", responseText);

      this.lastSuccess = `Mobile number set successfully: ${mobile}`;
    } catch (error) {
      console.error("Error setting E2E mobile:", error);
      this.lastError = error instanceof Error ? error.message : "Unknown error occurred";
    } finally {
      this.isSettingMobile = false;
    }
  }

  /**
   * Set Sumsub verification data via internal API
   * Similar to the E2E test pattern from 03-sole-trader-flow.spec.ts
   */
  async setSumsub(): Promise<void> {
    this.isSettingSumsub = true;
    this.lastError = null;
    this.lastSuccess = null;

    try {
      const alias = this.getUserAlias();
      if (!alias) {
        throw new Error("User alias not found. Please authenticate first.");
      }

      const backendEnv = this.getBackendEnv();
      const sumsubUrl = `https://formations-private-${backendEnv}.anna.money/api/internal/e2e/set-sumsub`;

      console.log("Setting Sumsub verification data");
      console.log("SumSub URL:", sumsubUrl);
      console.log("Using alias:", alias);

      const response = await fetch(sumsubUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          alias,
          is_passed_verifications: true,
          first_name: "John",
          last_name: "Doe",
          dob: "1990-01-01",
          nationality_code: "GBP",
        }),
      });

      console.log("Set Sumsub response status:", response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Failed to set Sumsub:", response.status, errorText);
        throw new Error(`Failed to set Sumsub: ${response.status} - ${errorText}`);
      }

      const responseText = await response.text();
      console.log("Set Sumsub response body:", responseText);

      this.lastSuccess = "Sumsub verification data set successfully";
    } catch (error) {
      console.error("Error setting Sumsub:", error);
      this.lastError = error instanceof Error ? error.message : "Unknown error occurred";
    } finally {
      this.isSettingSumsub = false;
    }
  }

  /**
   * Helper to wait/delay execution
   */
  private async wait(ms: number): Promise<void> {
    await new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Helper to fill and trigger change event on input field
   */
  private fillInput(selector: string, value: string): boolean {
    const input = document.querySelector<HTMLInputElement>(selector);
    if (input) {
      // Set the value
      input.value = value;

      // Trigger React's onChange event
      const event = new Event("input", { bubbles: true });
      input.dispatchEvent(event);

      // Also trigger change event for some forms
      const changeEvent = new Event("change", { bubbles: true });
      input.dispatchEvent(changeEvent);

      console.log(`Filled ${selector} with: ${value}`);
      return true;
    }
    console.warn(`Input not found: ${selector}`);
    return false;
  }

  /**
   * Helper to click the Continue button
   * Similar to continueNext from E2E tests
   */
  private async clickContinue(): Promise<boolean> {
    // Wait for button to be rendered
    await this.wait(500);

    // Try to find the Continue button using data-test-id (primary selector)
    const continueButton = document.querySelector<HTMLButtonElement>('[data-test-id="submit-button"]');

    if (continueButton) {
      // Check if button is visible and enabled
      const isVisible =
        continueButton.offsetWidth > 0 &&
        continueButton.offsetHeight > 0 &&
        window.getComputedStyle(continueButton).visibility !== "hidden";

      if (isVisible && !continueButton.disabled) {
        // Scroll into view if needed
        continueButton.scrollIntoView({ behavior: "smooth", block: "center" });
        await this.wait(300);

        // Click the button
        continueButton.click();
        console.log("Clicked Continue button");

        // Wait for page stabilization (similar to E2E tests)
        await this.wait(500);

        return true;
      } else {
        console.warn("Continue button found but not clickable (disabled or hidden)");
        return false;
      }
    }

    console.warn("Continue button not found");
    return false;
  }

  /**
   * Fill director information based on region (UK or AU)
   * Automatically fills in form fields similar to E2E tests
   */
  async fillDirectorInfo(): Promise<void> {
    this.isFillingDirector = true;
    this.lastError = null;
    this.lastSuccess = null;

    try {
      const config = getConfig();
      const isAU = config.region === Region.AU;

      console.log(`Filling director info for region: ${config.region}`);

      if (isAU) {
        // AU Flow - fill common fields
        // First name and last name
        this.fillInput('input[placeholder="Enter your first name"]', "John");
        this.fillInput('input[placeholder="Enter your last name"]', "Doe");

        // Try alternative selectors for first/last name
        const firstNameInput = document.querySelector<HTMLInputElement>(
          'input[name*="first"], input[placeholder*="first name" i]',
        );
        if (firstNameInput && !firstNameInput.value) {
          this.fillInput('input[name*="first"], input[placeholder*="first name" i]', "John");
        }

        const lastNameInput = document.querySelector<HTMLInputElement>(
          'input[name*="last"], input[placeholder*="last name" i]',
        );
        if (lastNameInput && !lastNameInput.value) {
          this.fillInput('input[name*="last"], input[placeholder*="last name" i]', "Doe");
        }

        // Date of birth (AU format: DD/MM/YYYY)
        const dobInput = document.querySelector<HTMLInputElement>('input[placeholder="DD/MM/YYYY"]');
        if (dobInput) {
          this.fillInput('input[placeholder="DD/MM/YYYY"]', "01/01/2000");
        }

        // City/town
        const cityInput = document.querySelector<HTMLInputElement>(
          'input[placeholder*="city" i], input[placeholder*="town" i]',
        );
        if (cityInput) {
          this.fillInput('input[placeholder*="city" i], input[placeholder*="town" i]', "Testing");
        }

        // TFN/identifier
        const tfnInput = document.querySelector<HTMLInputElement>('input[placeholder*="identifier" i]');
        if (tfnInput) {
          this.fillInput('input[placeholder*="identifier" i]', "123456782");
        }

        // Wait a bit for React to process all changes
        await this.wait(300);

        // Try to click Continue button
        const clicked = await this.clickContinue();
        if (clicked) {
          this.lastSuccess = "Director info filled (AU) and Continue clicked!";
        } else {
          this.lastSuccess = "Director info filled (AU). Click Continue manually.";
        }
      } else {
        // UK Flow
        // First name and last name
        this.fillInput('input[placeholder="Enter your first name"]', "John");
        this.fillInput('input[placeholder="Enter your last name"]', "Doe");

        // Date of birth (UK format: DD/MM/YYYY)
        const dobInput = document.querySelector<HTMLInputElement>('[data-test-id="input"]');
        if (dobInput?.placeholder.includes("/")) {
          this.fillInput('[data-test-id="input"]', "01/01/1990");
        }

        // Nationality
        const nationalityInput = document.querySelector<HTMLInputElement>('[data-test-id="input"]');
        if (nationalityInput && !nationalityInput.value) {
          setTimeout(() => {
            const natInput = document.querySelector<HTMLInputElement>('input[placeholder*="nationality" i]');
            if (natInput) {
              this.fillInput('input[placeholder*="nationality" i]', "British");
            }
          }, 500);
        }

        // Address fields (manual entry)
        const buildingInput = document.querySelector<HTMLInputElement>('input[name="address.premise"]');
        if (buildingInput) {
          this.fillInput('input[name="address.premise"]', "1");
          this.fillInput('input[name="address.street"]', "Test Street");
          this.fillInput('input[name="address.town"]', "London");
          this.fillInput('input[name="address.postcode"]', "SW1A 1AA");
        }

        // Wait a bit for React to process all changes
        await this.wait(300);

        // Try to click Continue button
        const clicked = await this.clickContinue();
        if (clicked) {
          this.lastSuccess = "Director info filled (UK) and Continue clicked!";
        } else {
          this.lastSuccess = "Director info filled (UK). Click Continue manually.";
        }
      }

      console.log("Director info filling complete");
    } catch (error) {
      console.error("Error filling director info:", error);
      this.lastError = error instanceof Error ? error.message : "Failed to fill director info";
    } finally {
      this.isFillingDirector = false;
    }
  }

  /**
   * Clear messages
   */
  clearMessages(): void {
    this.lastError = null;
    this.lastSuccess = null;
  }
}
